/**
 * React Query key factories for contacts and organizations.
 *
 * A LEAF module on purpose: it imports only types, so importing it pulls in no
 * hooks, no api client and no React. That matters because `useContacts` and
 * `useOrganizations` are mocked with partial factories in ~17 spec files
 * (`vi.mock('../hooks/useContacts', () => ({ useContact: …, useUpdateContact: … }))`),
 * and a partial mock replaces the WHOLE module — so anything else importing a key
 * factory from there gets `undefined` and dies with "No 'contactKeys' export is
 * defined on the mock".
 *
 * That is not hypothetical: `useRelationshipEdit` imported `contactKeys` from
 * `useContacts`, and because `ContactDetailPage` mounts it transitively, three
 * unrelated detail-page specs went red on the merge tree (WI e57adbc4). Fixing it
 * by adding `contactKeys` to seventeen mock factories would have been the
 * copy-paste that breaks again for the next author — the same shape
 * `.claude/rules/rules-testing.md` records for the `react-i18next` partial mock.
 *
 * So: NEW consumers import keys from here. `useContacts` / `useOrganizations`
 * re-export them, unchanged, for the call sites that already do.
 */
import type { ListContactsParams, ListOrganizationsParams } from '../types';

export const contactKeys = {
  all: ['contacts'] as const,
  list: (params?: ListContactsParams) => [...contactKeys.all, 'list', params] as const,
  detail: (id: string) => [...contactKeys.all, 'detail', id] as const,
};

export const organizationKeys = {
  all: ['organizations'] as const,
  list: (params?: ListOrganizationsParams) =>
    [...organizationKeys.all, 'list', params] as const,
  detail: (id: string) => [...organizationKeys.all, 'detail', id] as const,
  domainMatch: (domain: string) =>
    [...organizationKeys.all, 'domain-match', domain] as const,
};
